import { useRouter } from 'next/router';
import { ComponentProps, useContext, useMemo } from 'react';

import { NftItem } from '@/components/common/NftItem';
import { AuthContext } from '@/components/context/AuthContext';
import { Paths } from '@/constants/path';
import { useMyNftsQuery } from '@/graphql/generated';
import { createPath } from '@/utils/path';

export type NftItemType = ComponentProps<typeof NftItem> & { id: string };

export const useMyNfts = () => {
  const { currentUser } = useContext(AuthContext);

  const router = useRouter();
  const { lng } = router.query;
  const [myNftsData] = useMyNftsQuery({
    pause: !currentUser,
  });

  const myNfts = useMemo<Array<NftItemType> | undefined>(() => {
    return myNftsData?.data?.webMyNfts
      .map<NftItemType | undefined>((nft) => {
        if (nft.lot.product.targetObject.__typename === 'Contributor') {
          const company = nft.lot.product.targetObject.company;
          const target = nft.lot.product.targetObject;
          const address = target.prefecture;
          const addressEn = target.prefectureEn ?? target.prefecture;

          return {
            id: nft.id,
            companyName:
              lng === 'ja' && company
                ? company.name
                : company
                ? company.nameEn ?? company.name
                : '',
            address: lng === 'ja' ? address : addressEn,
            power:
              target.plantCapacityNumber *
              target.co2PowerPerUnit *
              (target.daylightHours ?? 0),
            count: nft.yellNftSummary?.yellCount ?? 0,
            imageUrl: nft.contentUrl,
            nftName:
              lng === 'ja'
                ? nft.lot.product.name
                : nft.lot.product.nameEn ?? nft.lot.product.name,
            onClick: () => {
              router.push(
                `${createPath({
                  path: Paths.v2MyNft,
                  params: { id: nft.id },
                })}?fromNfts=true`
              );
            },
          };
        }
      })
      .filter((nft): nft is NonNullable<typeof nft> => nft !== undefined);
  }, [lng, myNftsData?.data?.webMyNfts, router]);

  return {
    myNfts,
  };
};
